import React from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, User, ArrowLeft, ExternalLink, ImageOff } from "lucide-react";
import { Reveal, Eyebrow, PageHeader } from "../components/common";
import { useSiteData } from "../context/SiteData";
import { findImage, resolveImage } from "../lib/api";

export default function LocationDetail() {
  const { id } = useParams();
  const { data, loading, loaded } = useSiteData();
  const outlet = loaded ? data.outlets.find((o) => o.id === id) : null;
  const photo = loaded ? resolveImage(findImage(data.images, "location-" + id)?.path, null) : null;

  if (loading) {
    return (
      <section className="max-w-3xl mx-auto px-6 py-32 text-center text-[var(--rs-muted)]">Loading outlet details…</section>
    );
  }

  if (!outlet) {
    return (
      <section className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-head font-extrabold text-3xl">Outlet not found</h1>
        <p className="text-[var(--rs-muted)] mt-4">{loaded ? "This outlet doesn't exist or was removed." : "Couldn't load outlet data — the backend may be unreachable."}</p>
        <Link to="/locations" className="btn-primary rounded-lg px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 mt-8"><ArrowLeft size={16} /> Back to Locations</Link>
      </section>
    );
  }

  return (
    <>
      <PageHeader crumb={outlet.name} title={outlet.name} intro={`${outlet.tag} · ${outlet.town}`} />

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
        <Reveal>
          <Link to="/locations" className="inline-flex items-center gap-2 text-sm font-medium text-[var(--rs-blue)] hover:underline mb-8"><ArrowLeft size={15} /> Back to all locations</Link>
        </Reveal>

        <Reveal delay={80}>
          {photo ? (
            <img src={photo} alt={outlet.name} className="w-full h-[320px] object-cover rounded-2xl mb-6" />
          ) : (
            <div className="w-full h-[220px] rounded-2xl mb-6 border border-dashed border-[var(--rs-border)] bg-[var(--rs-bg)] flex flex-col items-center justify-center gap-2 text-[var(--rs-muted)]">
              <ImageOff size={28} />
              <span className="text-sm">Photo not yet available</span>
            </div>
          )}
        </Reveal>

        <Reveal delay={100}>
          <div className="bg-white border border-[var(--rs-border)] rounded-2xl p-8 grid sm:grid-cols-2 gap-8">
            <div className="flex items-start gap-3">
              <MapPin size={19} className="text-[var(--rs-blue)] mt-0.5 shrink-0" />
              <div>
                <h3 className="font-head font-semibold text-sm text-[var(--rs-muted)] uppercase tracking-wide">Address</h3>
                <p className="mt-1.5 text-base">{outlet.address}</p>
                <p className="text-sm text-[var(--rs-muted)] mt-0.5">{outlet.town}</p>
                {outlet.mapsUrl && (
                  <a href={outlet.mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--rs-blue)] hover:underline mt-3">
                    Get Directions <ExternalLink size={13} />
                  </a>
                )}
              </div>
            </div>

            {outlet.phone && (
              <div className="flex items-start gap-3">
                <Phone size={19} className="text-[var(--rs-blue)] mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-head font-semibold text-sm text-[var(--rs-muted)] uppercase tracking-wide">Phone</h3>
                  <a href={`tel:${outlet.telHref || outlet.phone}`} className="mt-1.5 text-base block hover:text-[var(--rs-blue)]">{outlet.phone}</a>
                </div>
              </div>
            )}

            {outlet.email && (
              <div className="flex items-start gap-3">
                <Mail size={19} className="text-[var(--rs-blue)] mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-head font-semibold text-sm text-[var(--rs-muted)] uppercase tracking-wide">Email</h3>
                  <a href={`mailto:${outlet.email}`} className="mt-1.5 text-base block hover:text-[var(--rs-blue)] break-all">{outlet.email}</a>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Clock size={19} className="text-[var(--rs-blue)] mt-0.5 shrink-0" />
              <div>
                <h3 className="font-head font-semibold text-sm text-[var(--rs-muted)] uppercase tracking-wide">Store Timings</h3>
                <p className="mt-1.5 text-base text-[var(--rs-muted)]">{outlet.timings || "Not yet confirmed"}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <User size={19} className="text-[var(--rs-blue)] mt-0.5 shrink-0" />
              <div>
                <h3 className="font-head font-semibold text-sm text-[var(--rs-muted)] uppercase tracking-wide">Outlet In-Charge</h3>
                <p className="mt-1.5 text-base text-[var(--rs-muted)]">{outlet.inCharge || "Staff details to be added"}</p>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={140}>
          <div className="mt-10">
            <Eyebrow>Have a question about this outlet?</Eyebrow>
            <Link to="/contact" className="btn-outline rounded-lg px-6 py-3 text-sm font-semibold inline-block mt-4">Contact Head Office</Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
